import { useLayoutEffect, type RefObject } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useLocation, useNavigationType } from "react-router";

type Position = { y: number; href: string | null; offset: number; focus: string | null };
type Store = { userId: string | null; generation: number; positions: Map<string, Position> };
const stores = new WeakMap<QueryClient, Store>();
function storeFor(client: QueryClient): Store {
  let store = stores.get(client);
  if (!store) {
    store = { userId: null, generation: 0, positions: new Map() };
    stores.set(client, store);
    const current = store;
    client.getQueryCache().subscribe((event) => {
      if (event.type === "removed" && event.query.queryKey[0] === "identity" && event.query.queryKey[1] === "session") {
        current.positions.clear();
        current.generation++;
      }
    });
  }
  return store;
}

/** Only route/identity keys and viewport coordinates; no messages or browser storage. */
export function usePhoneListMemory(ref: RefObject<HTMLElement | null>, enabled: boolean, userId: string | null, householdId: string | null, petId: string | null, homeId: string | null = null) {
  const client = useQueryClient();
  const location = useLocation();
  const navigation = useNavigationType();
  const key = userId && petId ? JSON.stringify([userId, householdId, homeId, petId, location.key]) : null;
  useLayoutEffect(() => {
    const store = storeFor(client);
    if (store.userId !== userId) {
      store.positions.clear();
      store.generation++;
      store.userId = userId;
    }
    const el = ref.current;
    if (!enabled || !key || !el) return;
    const generation = store.generation;
    const saved = store.positions.get(key);
    let frame = 0;
    let interacting = false;
    let restored = false;
    const links = () => Array.from(el.querySelectorAll<HTMLAnchorElement>("a[href]"));
    const find = (href: string | null) => links().find((a) => a.getAttribute("href") === href);
    const capture = (event?: Event) => {
      if (store.generation !== generation) return;
      const anchor = links().find((a) => a.getBoundingClientRect().bottom > 80);
      const clicked = event?.target instanceof Element ? event.target.closest("a[href]") : null;
      const focused = el.contains(document.activeElement) ? document.activeElement?.closest("a[href]") : null;
      store.positions.set(key, { y: window.scrollY, href: anchor?.getAttribute("href") ?? null, offset: anchor?.getBoundingClientRect().top ?? 0, focus: (clicked ?? focused)?.getAttribute("href") ?? null });
      if (store.positions.size > 32) store.positions.delete(store.positions.keys().next().value!);
    };
    const restore = () => {
      if (interacting || store.generation !== generation) return;
      const anchor = saved ? find(saved.href) : null;
      const y = saved ? anchor ? window.scrollY + anchor.getBoundingClientRect().top - saved.offset : saved.y : 0;
      window.scrollTo(0, Math.max(0, y));
      if (!restored && navigation === "POP" && saved?.focus && (document.activeElement === document.body || !document.activeElement?.isConnected)) {
        const focus = find(saved.focus);
        if (focus && focus.getClientRects().length && !focus.closest('[hidden], [inert], [aria-disabled="true"]')) focus.focus({ preventScroll: true });
      }
      restored = true;
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(restore); };
    const interact = () => { interacting = true; cancelAnimationFrame(frame); };
    // The parent app owns general ScrollRestoration. Apply the identity-bound list
    // anchor after that layout pass, and again only for passive content resizing.
    schedule();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(el);
    window.addEventListener("scroll", capture, { passive: true });
    el.addEventListener("click", capture, true);
    window.addEventListener("wheel", interact, { passive: true });
    window.addEventListener("touchstart", interact, { passive: true });
    window.addEventListener("keydown", interact);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("scroll", capture);
      el.removeEventListener("click", capture, true);
      window.removeEventListener("wheel", interact);
      window.removeEventListener("touchstart", interact);
      window.removeEventListener("keydown", interact);
    };
  }, [client, enabled, key, userId, ref, navigation]);
}
